import type { TicketMessageUnion } from '@wowpress/types'; // TicketMessageUnion 타입 임포트

// Firestore Timestamp 또는 Date 객체를 안전하게 JS Date 객체로 변환하는 함수
export const getJsDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  // Firestore Timestamp 객체 확인 (toDate 메서드 존재 여부)
  if (typeof timestamp.toDate === 'function') {
    try {
      return timestamp.toDate();
    } catch (e) {
      console.error('Error calling toDate() on timestamp:', timestamp, e);
      return null;
    }
  }
  // 이미 Date 객체인 경우
  if (timestamp instanceof Date) {
    // 유효한 Date 객체인지 확인
    return !isNaN(timestamp.getTime()) ? timestamp : null;
  }
  // 숫자 (밀리초)인 경우
  if (typeof timestamp === 'number') {
    const date = new Date(timestamp);
    return !isNaN(date.getTime()) ? date : null;
  }
  // 문자열인 경우 Date 객체로 변환 시도
  if (typeof timestamp === 'string') {
     try {
       const date = new Date(timestamp);
       // 유효한 날짜인지 확인 (Invalid Date 방지)
       if (!isNaN(date.getTime())) {
         return date;
       }
     } catch (e) {
       console.error('Error parsing date string:', timestamp, e);
     }
  }

  console.warn('Could not convert timestamp to valid Date:', timestamp);
  return null;
};


// 날짜 구분선 포맷 함수
export const formatDateSeparator = (dateString: string): string => {
  try {
    const today = new Date();
    const date = new Date(dateString); // dateString이 'YYYY-MM-DD' 형식이라고 가정
    if (isNaN(date.getTime())) {
        console.warn('Invalid date string for separator:', dateString);
        return dateString;
    }

    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    const todayDate = today.getDate();

    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();
    const dateDay = date.getDate();

    if (dateYear === todayYear && dateMonth === todayMonth && dateDay === todayDate) {
      return '오늘';
    }
    // 어제 확인 (선택적)
    // const yesterday = new Date(today);
    // yesterday.setDate(today.getDate() - 1);
    // if (date.toDateString() === yesterday.toDateString()) {
    //   return '어제';
    // }

    // Intl.DateTimeFormat 사용 권장 (더 나은 현지화 지원)
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    }).format(date);
  } catch (e) {
    console.warn('Error formatting date separator:', dateString, e);
    return dateString; // 파싱 실패 시 원본 반환
  }
};

// 파일 아이콘 가져오기
export const getFileIcon = (mimeType?: string): string => {
  if (!mimeType) return 'i-heroicons-document'; // 기본 아이콘
  const lowerMimeType = mimeType.toLowerCase();

  if (lowerMimeType.startsWith('image/')) return 'i-heroicons-photo';
  if (lowerMimeType.startsWith('video/')) return 'i-heroicons-video-camera';
  if (lowerMimeType.startsWith('audio/')) return 'i-heroicons-musical-note';
  if (lowerMimeType === 'application/pdf') return 'i-heroicons-document-text'; // PDF 정확히 매칭
  if (lowerMimeType.includes('zip') || lowerMimeType.includes('archive') || lowerMimeType.includes('rar') || lowerMimeType.includes('7z')) return 'i-heroicons-archive-box';

  // MS Office 및 OpenXML 형식
  if (lowerMimeType.startsWith('application/vnd.ms-excel') || lowerMimeType.startsWith('application/vnd.openxmlformats-officedocument.spreadsheetml')) return 'i-heroicons-table-cells';
  if (lowerMimeType.startsWith('application/msword') || lowerMimeType.startsWith('application/vnd.openxmlformats-officedocument.wordprocessingml')) return 'i-heroicons-document-text';
  if (lowerMimeType.startsWith('application/vnd.ms-powerpoint') || lowerMimeType.startsWith('application/vnd.openxmlformats-officedocument.presentationml')) return 'i-heroicons-presentation-chart-line';

  // 기타 텍스트 기반 파일 (예시)
  if (lowerMimeType.startsWith('text/')) return 'i-heroicons-document-text';

  return 'i-heroicons-document'; // 그 외 모든 경우 기본 아이콘
};

// 파일 크기 포맷
export const formatFileSize = (bytes?: number): string => {
    // bytes가 유효한 숫자인지 확인
    if (bytes === undefined || bytes === null || typeof bytes !== 'number' || isNaN(bytes) || bytes < 0) {
        // console.warn('Invalid bytes value for formatting:', bytes);
        return '0 B'; // 또는 빈 문자열 '', 오류 메시지 등
    }
    if (bytes === 0) return '0 B'; // 0 바이트 처리

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']; // 더 큰 단위 추가 가능

    // Math.log(0)은 -Infinity 이므로 bytes가 0일 때 분기 처리 필요
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // 계산된 크기와 단위를 반환. toFixed(1)은 소수점 첫째 자리까지 표시
    // parseFloat을 사용하여 불필요한 .0 제거 (예: 1.0 KB -> 1 KB)
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};


// 날짜 포맷 함수 (YYYY.MM.DD)
export const formatDate = (timestamp: any): string => {
  const date = getJsDate(timestamp); // getJsDate 재사용
  if (!date) return '';
  // 'ko-KR' 로케일을 사용하여 YYYY. MM. DD. 형식을 얻고, 마지막 점 제거 및 공백 제거
  try {
      return date.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit'}).replace(/\.$/, '').replace(/\s/g, '');
  } catch (e) {
      console.error('Error formatting date:', timestamp, e);
      return '';
  }
};


// 스크롤이 맨 아래에 있는지 확인하는 헬퍼 함수
export const isScrolledToBottom = (container: HTMLElement | null, threshold: number = 10): boolean => {
  if (!container) return false;
  // scrollTop, scrollHeight, clientHeight 값이 정수가 아닐 수 있으므로 반올림 또는 Math.ceil 사용 고려
  const nearBottom = Math.ceil(container.scrollHeight) - Math.ceil(container.scrollTop) - container.clientHeight <= threshold;
  // console.log(`scrollHeight: ${container.scrollHeight}, scrollTop: ${container.scrollTop}, clientHeight: ${container.clientHeight}, nearBottom: ${nearBottom}`);
  return nearBottom;
}; 


// Firebase Firestore 타임스탬프 변환 유틸리티 함수
export const getDateFromTimestamp = (timestamp: any): Date | null => {
  if (!timestamp) return null;

  // Firestore 타임스탬프 객체인 경우 (toDate 메서드가 있음)
  if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
  }

  // ISO 문자열인 경우
  if (typeof timestamp === 'string') {
      return new Date(timestamp);
  }

  // Date 객체인 경우
  if (timestamp instanceof Date) {
      return timestamp;
  }

  // 숫자(밀리초)인 경우
  if (typeof timestamp === 'number') {
      return new Date(timestamp);
  }

  return null;
};

// 메시지 타임스탬프에서 날짜 부분만 추출 (YYYY-MM-DD)
export const getDateStringFromTimestamp = (timestamp: any): string | undefined => {
  const date = getDateFromTimestamp(timestamp);
  if (!date) return '';

  return date.toISOString().split('T')[0];
};
